import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe, faLink } from "@fortawesome/pro-regular-svg-icons";
import { Badge, TBadgeType } from "components/badge/Badge";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import Input from "components/form/input";
import { Select } from "components/select/index";
import "./websiteBuilderSettings.scss";
import { useWebsiteBuilderLauncher } from "./useWebsiteBuilderLauncher";

type TWebsiteDomainStatus = "connected" | "not-connected";

interface IWebsiteDomain {
  url: string;
  status: TWebsiteDomainStatus;
}

type TVersionHistoryStatus = "published" | "saved";

interface IVersionHistory {
  id: number;
  date: string;
  time: string;
  firstName: string;
  lastName: string;
  status: TVersionHistoryStatus;
}

type TVersionHistoryFilter = "all" | TVersionHistoryStatus;

const versionHistoryFilters: TVersionHistoryFilter[] = ["all", "published", "saved"];

export default function WebsiteBuilderSettings() {
  const { Option } = Select;
  const { t } = useTranslation();
  const websiteBuilderLauncher = useWebsiteBuilderLauncher();

  const [websiteExists] = useState<boolean>(false);

  const [websiteDomain] = useState<IWebsiteDomain>({
    url: "https://rebelcreekgolf.com",
    status: "connected",
  });

  const [versionHistoryFilter, setVersionHistoryFilter] = useState<TVersionHistoryFilter>("all");

  const [versionHistories] = useState<IVersionHistory[]>([
    {
      id: 1,
      date: "May 28, 2025",
      time: "9:51pm",
      firstName: "Adrian",
      lastName: "Barsi",
      status: "published",
    },
    {
      id: 2,
      date: "May 28, 2025",
      time: "9:51pm",
      firstName: "Adrian",
      lastName: "Barsi",
      status: "saved",
    },
    {
      id: 3,
      date: "May 28, 2025",
      time: "9:51pm",
      firstName: "Adrian",
      lastName: "Barsi",
      status: "saved",
    },
  ]);

  const filteredVersionHistories: IVersionHistory[] = useMemo(() => {
    if (versionHistories?.length > 0) {
      return versionHistories.filter(
        versionHistory => versionHistoryFilter === "all" || versionHistory.status === versionHistoryFilter,
      );
    } else {
      return [];
    }
  }, [versionHistories, versionHistoryFilter]);

  function renderWebsiteDomainStatusBadge(status: TWebsiteDomainStatus) {
    let statusType: TBadgeType = "gray";
    let statusText = "";

    if (status === "connected") {
      statusType = "success";
      statusText = t("Connected");
    } else if (status === "not-connected") {
      statusType = "gray";
      statusText = t("Not Connected");
    }

    return (
      <Badge iconLeft={<FontAwesomeIcon className="settings-domain-status-icon" icon={faCircle} />} type={statusType}>
        {statusText}
      </Badge>
    );
  }

  return (
    <Page
      narrow
      title={t("Settings")}
      subtitle={t("Manage your website, pages, and domain.")}
      primaryAction={{
        content: websiteBuilderLauncher.launchWebsiteBuilderContent(websiteExists),
        action: () => websiteBuilderLauncher.launchWebsiteBuilder(websiteExists),
      }}
      multipleActionDropdownAction={{
        label: t("Quick actions"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: "",
              handler: () => {},
              icon: undefined,
            },
          ],
        },
      }}
    >
      <div className="website-builder-settings">
        <Card
          title={t("Connect URL")}
          subtitle={t("Connect your custom domain to boost website visibility.")}
        >
          <Card.Section>
            <div className="settings-domain-card-container">
              <Card>
                <Card.Section>
                  <div className="settings-domain-card-content">
                    <div className="settings-domain-container">
                      <FontAwesomeIcon className="settings-domain-link-icon" icon={faLink} />
                      <Input
                        className="settings-domain-input"
                        prefix={<FontAwesomeIcon icon={faGlobe} />}
                        labelHidden
                        placeholder="https://yourwebsite.com"
                        readOnly
                        value={websiteDomain.url}
                      />
                    </div>
                    {renderWebsiteDomainStatusBadge(websiteDomain.status)}
                  </div>
                </Card.Section>
              </Card>
            </div>
          </Card.Section>
        </Card>
        <Card
          title={t("Version history")}
          subtitle={t("Manage the various versions of your website.")}
        >
          <Card.Section>
            {versionHistories?.length > 0 && (
              <>
                <div className="settings-version-history-filter-container">
                  <Select defaultValue={versionHistoryFilter} onChange={setVersionHistoryFilter}>
                    {versionHistoryFilters.map((filter, index) => {
                      let statusText = "";

                      if (filter === "all") {
                        statusText = "All";
                      } else if (filter === "published") {
                        statusText = "Published";
                      } else if (filter === "saved") {
                        statusText = "Saved";
                      }

                      return (
                        <Option key={index} value={filter}>
                          {statusText} versions{/* TODO: Translation */}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
                <div className="settings-version-histories">
                  {filteredVersionHistories.map(versionHistory => {
                    let statusType: TBadgeType = "gray";
                    let statusText = "";

                    if (versionHistory.status === "published") {
                      statusType = "success";
                      statusText = "Published";
                    } else if (versionHistory.status === "saved") {
                      statusType = "gray";
                      statusText = "Saved";
                    }

                    return (
                      <div key={versionHistory.id}>
                        <Card>
                          <Card.Section>
                            <div className="settings-version-history">
                              <div>
                                <div className="settings-version-history-date">
                                  {versionHistory.date} at {versionHistory.time}
                                </div>
                                {/* TODO: Translation */}
                                <div className="settings-version-history-author">
                                  Last modified by {versionHistory.firstName} {versionHistory.lastName}
                                </div>
                                {/* TODO: Translation */}
                              </div>
                              <Badge type={statusType}>{statusText}</Badge>
                            </div>
                          </Card.Section>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card.Section>
        </Card>
      </div>
    </Page>
  );
}
